import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getAllCubeTestReports, printCubeTestReport } from "../../services/CubeTestReportServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import {
    buildCubeTestReportQueryParams,
    DEFAULT_LIST_VIEW_CONFIG,
} from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig, normalizeCubeTestListViewConfig } from "../../config/listViewSettingsConfig";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard } from "../../utils/listSummaryUtils";
import moment from 'moment';

function mapCubeTestReportRows(list = []) {
    return list.map((ctr) => {
        const day7Avg = Number(ctr.day7_avg_strength) || 0;
        const day28Avg = Number(ctr.day28_avg_strength) || 0;
        const isClosed = !!ctr.is_closed;
        const day7Completed = isClosed || !!ctr.day7_completed || day7Avg > 0;
        const day28Completed = isClosed || !!ctr.day28_completed || day28Avg > 0;
        return {
            ...ctr,
            plant_alias: ctr.plant?.alias_name ?? '',
            report_no: `${ctr.prefix || ''}${ctr.report_no || ''}`,
            customer_name: ctr.consignee?.customer?.name ?? '',
            salesrep_name: ctr.consignee?.customer?.salesrep?.name ?? '',
            consignee_name: ctr.consignee?.name ?? '',
            project_name: ctr.consignee?.project_name ?? '',
            status: isClosed ? 'Closed' : (ctr.status ? 'Active' : 'Inactive'),
            is_closed: isClosed,
            day7_completed: day7Completed,
            day28_completed: day28Completed,
            day7_avg_display: isClosed ? 'N/A' : (day7Completed ? day7Avg.toFixed(2) : '0.00'),
            day28_avg_display: isClosed ? 'N/A' : (day28Completed ? day28Avg.toFixed(2) : '0.00'),
        };
    });
}

const ageBadgeStyle = (completed) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: completed ? '#16a34a' : '#f97316',
});

function CubeTestReportTable() {
    const InitialData = {
        records: []
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const highlightCard = useMemo(() => {
        const todayCount = data.records.filter((row) => row.report_date === todayDateLabel).length;
        return buildGradientCard('Today Reports', todayCount, 'today');
    }, [data.records, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const activeRecords = data.records.filter((row) => row.status === 'Active');
        const day7Done = activeRecords.filter((row) => row.day7_completed).length;
        const day28Done = activeRecords.filter((row) => row.day28_completed).length;
        const day28Pending = activeRecords.filter((row) => !row.day28_completed).length;
        const cards = [
            buildGradientCard('Total Reports', data.records.length, 'blue'),
            buildGradientCard('7 Day Done', day7Done, 'teal'),
            buildGradientCard('28 Day Done', day28Done, 'purple'),
        ];
        if (day28Pending > 0) {
            cards.push(buildGradientCard('28 Day Pending', day28Pending, 'red'));
        }
        return cards;
    }, [data.records]);

    const columns = useMemo(
        () => [
            {
                fieldName: 'plant_alias',
                headerName: 'Plant'
            },
            {
                fieldName: "report_no",
                headerName: 'Report Number'
            },
            {
                fieldName: "report_date",
                headerName: 'Report Date'
            },
            {
                fieldName: "casting_date",
                headerName: 'Casting Date'
            },
            {
                fieldName: "day7_avg_display",
                headerName: '7th Day Avg',
                getCellStyle: (row) => ageBadgeStyle(row.day7_completed),
            },
            {
                fieldName: "day28_avg_display",
                headerName: '28th Day Avg',
                getCellStyle: (row) => ageBadgeStyle(row.day28_completed),
            },
            {
                fieldName: "customer_name",
                headerName: 'Customer Name'
            },
            {
                fieldName: "project_name",
                headerName: 'Site Name'
            },
            {
                fieldName: "grade",
                headerName: 'Grade'
            },
            {
                fieldName: 'salesrep_name',
                headerName: 'Marketting'
            },
            {
                fieldName: "status",
                headerName: 'Status',
                getCellStyle: (row) => {
                    if (row.status === 'Closed') {
                        return {
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: '#64748b',
                        };
                    }
                    return undefined;
                },
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
        [],
    );

    const onEditCubeTestReport = (ctrId) => {
       navigate(`/CubeTestReportEdit/${ctrId}/`);
    };

    const onPrintCubeTestReport = (ctrId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printCubeTestReport(cookies, ctrId)
        .then(response => {
            openPdfResponse(response, {
                title: 'Cube Test Report',
                downloadFileName: 'cube_test_report.pdf',
            });
            setIsLoading(false);
        })
        .catch(async (error) => {
            let message = error?.response?.data || error;
            // Blob error bodies need to be parsed for a readable message.
            if (error?.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const parsed = JSON.parse(text);
                    message = parsed.detail || parsed.message || parsed || text;
                } catch (parseError) {
                    message = error.message || 'Print failed';
                }
            }
            displayError(message, 'Print Failed');
            setIsLoading(false);
        });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(row => ({
                plant_alias: row.plant_alias,
                report_no: row.report_no,
                report_date: row.report_date,
                casting_date: row.casting_date,
                day7_avg_display: row.day7_avg_display,
                day28_avg_display: row.day28_avg_display,
                customer_name: row.customer_name,
                project_name: row.project_name,
                grade: row.grade,
                salesrep_name: row.salesrep_name,
                status: row.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Cube Test Report',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map(row => ({
                plant_alias: row.plant_alias,
                report_no: row.report_no,
                report_date: row.report_date,
                casting_date: row.casting_date,
                day7_avg_display: row.day7_avg_display,
                day28_avg_display: row.day28_avg_display,
                customer_name: row.customer_name,
                project_name: row.project_name,
                grade: row.grade,
                salesrep_name: row.salesrep_name,
                status: row.status,
            })),
            'CubeTestReportList.pdf'
        );
    };

    const loadCubeTestReportList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        const activeConfig = normalizeCubeTestListViewConfig(
            config || getDefaultListViewConfig('cube_test_report') || DEFAULT_LIST_VIEW_CONFIG
        );
        setIsLoading(true);
        getAllCubeTestReports(cookies, buildCubeTestReportQueryParams(activeConfig))
            .then((response) => {
                setData({ records: mapCubeTestReportRows(response?.cube_test_report_list || []) });
            })
            .catch((error) => {
                setData({ records: [] });
                displayError(error.response?.data ?? error.message, 'Failed to load cube test report list');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadCubeTestReportList(normalizeCubeTestListViewConfig(config));
    }, [loadCubeTestReportList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        getListViewSettings(cookies, 'cube_test_report')
            .then((response) => {
                loadCubeTestReportList(
                    normalizeCubeTestListViewConfig(
                        response.config || getDefaultListViewConfig('cube_test_report')
                    )
                );
            })
            .catch(() => {
                loadCubeTestReportList(getDefaultListViewConfig('cube_test_report'));
            });
    }, [cookies, cookies.myToken, loadCubeTestReportList]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="QUALITY CONTROL"
                    title="List of Cube Test Report"
                    viewKey="cube_test_report"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/CubeTestReport"
                    addNewLabel="New Cube Test Report"
                />

                <ListPageGradientSummary highlightCard={highlightCard} cards={summaryCards} />

                <div className="container mb-3" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        style={{ fontWeight: 'bold' }}
                        onClick={() => navigate('/CubeTestReport?pendingAge=7')}
                    >
                        7 Days Pending
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        style={{ fontWeight: 'bold' }}
                        onClick={() => navigate('/CubeTestReport?pendingAge=28')}
                    >
                        28 Days Pending
                    </button>
                </div>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of Cube test report'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCubeTestReport}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintCubeTestReport}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
            <button
                style={{ position: "relative", right: "40px" }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => { navigate("/CubeTestReport"); }}
            >
                Back
            </button>
        </div>
    );
}

export default CubeTestReportTable;
